import { auth } from '@/lib/firebase'
import * as Sentry from '@sentry/react'
import axios, { type InternalAxiosRequestConfig } from 'axios'

const client = axios.create()

// Per-request opt-out flag. Some endpoints (notably the WebAuthn
// challenge/verify pair used on the /login page) must be callable BEFORE the
// user has a Firebase session, so attaching a stale or absent bearer would
// either be wrong or — worse — cause the backend to 401 because it received a
// stale token from a prior session. Setting `skipAuth: true` on the axios
// config tells both the request and response interceptors below to leave
// these calls anonymous and to refrain from the 401-refresh retry.
export type SkipAuthConfig = { skipAuth?: boolean }

type AuthAwareConfig = InternalAxiosRequestConfig & SkipAuthConfig

client.interceptors.request.use((config) => {
  const span = Sentry.getActiveSpan()
  if (!span) return config

  if (!config.url?.startsWith('/api')) return config

  const ctx = span.spanContext()
  if (!ctx.traceFlags) return config

  const traceFlags = ctx.traceFlags.toString(2).padStart(2, '0')
  config.headers['traceparent'] =
    `00-${ctx.traceId}-${ctx.spanId}-${traceFlags}`
  return config
})

client.interceptors.request.use(async (config) => {
  if (!config.url?.startsWith('/api')) return config
  if ((config as AuthAwareConfig).skipAuth) return config
  const user = auth.currentUser
  if (!user) return config
  const token = await user.getIdToken()
  config.headers.Authorization = `Bearer ${token}`
  return config
})

type RetriableConfig = InternalAxiosRequestConfig &
  SkipAuthConfig & { _retriedAuth?: boolean }

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as RetriableConfig | undefined
    if (
      error.response?.status !== 401 ||
      !config ||
      config._retriedAuth ||
      config.skipAuth ||
      !config.url?.startsWith('/api')
    ) {
      return Promise.reject(error)
    }
    const user = auth.currentUser
    if (!user) return Promise.reject(error)
    config._retriedAuth = true
    const token = await user.getIdToken(true)
    config.headers.Authorization = `Bearer ${token}`
    return client(config)
  },
)

export default client

// decorator to wrap a function with a span
export function WithSpan<T, A extends any[]>(
  name: string,
  fn: (...args: A) => T,
): (...args: A) => T {
  return (...args: A) =>
    Sentry.startSpan({ name }, (_span) => {
      return fn(...args)
    })
}
